import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Tracks ids created inline within an entity picker and merges them into the
 * `selectedIds` array passed to `EntityPickerModal`. The modal's prop-addition
 * mirror (adjust-state-during-render against the `selectedIds` prop)
 * auto-selects the new ids in the draft, so the user lands on the picker with
 * the freshly created entity already checked.
 *
 * Resets when `open` transitions to false so stale ids don't leak across
 * sessions even if the parent closes the picker without routing through
 * `onOpenChange` (e.g. by flipping its own `setOpenPicker(null)`).
 */
export function useCreatedIdsMerge(selectedIds: string[], open: boolean) {
  const [createdIds, setCreatedIds] = useState<string[]>([]);

  const prevOpenRef = useRef(open);
  if (prevOpenRef.current !== open) {
    prevOpenRef.current = open;
    if (!open && createdIds.length > 0) setCreatedIds([]);
  }

  const mergedSelectedIds = useMemo(
    () => (createdIds.length === 0 ? selectedIds : [...selectedIds, ...createdIds]),
    [selectedIds, createdIds]
  );

  const registerCreated = useCallback((id: string) => {
    setCreatedIds((prev) => [...prev, id]);
  }, []);

  return { createdIds, mergedSelectedIds, registerCreated };
}
